import { Badge, Box, Flex, Grid, Group, Image, Modal, Paper, Text } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Carousel } from "../Carousel/Carousel";
import classNames from "./ImageGallery.module.css";

import { LoadedImage } from "@/components/elements";
import { IconFlag } from "@/components/icons";
import { FileUploader } from "@/components/widgets";
import { UserFeedback } from "@/components/widgets/UserFeedback";
import { env } from "@/config/env";

import type { Attachment } from "@/types/attachment";
import type { MantineStyleProps } from "@mantine/core";
import type { FC } from "react";

export type ImageGalleryProps = {
  images?: Attachment[];
  height?: number | string;
  maxPreviews?: number;
  modalTitle?: string;
  onUpload?: (files: any[]) => Promise<any>;
  onPlaceholderClick?: () => void;
  uploading?: boolean;
  isPlaceholderVisible?: boolean;
} & MantineStyleProps;

export const ImageGallery: FC<ImageGalleryProps> = ({
  images = [],
  height,
  maxPreviews = 5,
  modalTitle = "",
  onUpload,
  onPlaceholderClick,
  uploading,
  isPlaceholderVisible = false,
  ...props
}) => {
  const { t } = useTranslation();

  const [activeIndex, setActiveIndex] = useState(0);
  const [opened, { open, close }] = useDisclosure(false);
  const [feedbackOpened, { toggle: toggleFeedback }] = useDisclosure(false);
  const [feedbackImage, setFeedbackImage] = useState<Attachment | null>(null);

  const startPos = useRef<{ x: number; y: number } | null>(null);
  const threshold = 10; // pixel

  if (isPlaceholderVisible && (!images || images.length === 0)) {
    if (onPlaceholderClick) {
      return (
        <Box w="100%" style={{ cursor: "pointer" }} onClick={onPlaceholderClick} {...props}>
          <Box style={{ pointerEvents: "none" }}>
            <FileUploader
              accept={["image/*"]}
              maxSize={10 * 1024 * 1024}
              multiple
              labels={{ dropzoneDescription: t("documents.upload_description") }}
              loading={uploading}
              hideSaveButton
              onSave={async () => {}}
            />
          </Box>
        </Box>
      );
    }

    return (
      <Box w="100%" {...props}>
        <FileUploader
          accept={["image/*"]}
          maxSize={10 * 1024 * 1024}
          multiple
          labels={{ dropzoneDescription: t("documents.upload_description") }}
          loading={uploading}
          onCancel={() => {}}
          onSave={async (files) => {
            if (!onUpload) return;
            await onUpload(files);
          }}
        />
      </Box>
    );
  }

  const showPreviews = Math.min(maxPreviews, images.length);
  const hasMoreImages = images.length > maxPreviews;
  const moreImagesCount = images.length - maxPreviews;

  const onSlideChange = (index: number) => {
    setActiveIndex(index);
  };

  const handleStart = (e: React.MouseEvent | React.TouchEvent) => {
    const point = "touches" in e ? e.touches[0] : (e as React.MouseEvent);
    startPos.current = { x: point.clientX, y: point.clientY };
  };

  const handleEnd = (e: React.MouseEvent | React.TouchEvent, callback: () => void) => {
    if (!startPos.current) return;

    const point = "changedTouches" in e ? e.changedTouches[0] : (e as React.MouseEvent);

    const dx = Math.abs(point.clientX - startPos.current.x);
    const dy = Math.abs(point.clientY - startPos.current.y);

    if (dx < threshold && dy < threshold) {
      const target = ("target" in e ? e.target : null) as Element | null;
      if (target?.closest("[data-indicator]")) {
        startPos.current = null;
        return;
      }
      callback();
    }

    startPos.current = null;
  };

  return (
    <>
      <Box w="100%" {...props}>
        <Carousel
          withControls={false}
          withIndicators={images.length > 1}
          height={height}
          initialSlide={activeIndex}
          onSlideChange={onSlideChange}
          slideSize="100%"
          pb={0}
          slides={images.map((image, index) => (
            <Carousel.Slide key={index}>
              <Box style={{ position: "relative" }}>
                {image.path.includes(env.API_URL) ? (
                  <LoadedImage
                    image={image}
                    onMouseDown={handleStart}
                    onTouchStart={handleStart}
                    onMouseUp={(e) => handleEnd(e, open)}
                    onTouchEnd={(e) => handleEnd(e, open)}
                  />
                ) : (
                  <>
                    <Image
                      src={image.path}
                      //src={getImageContent(image.path)}
                      alt={image.name}
                      className={classNames.mainImage}
                      radius="md"
                      fit="cover"
                      onMouseDown={handleStart}
                      onTouchStart={handleStart}
                      onMouseUp={(e) => handleEnd(e, open)}
                      onTouchEnd={(e) => handleEnd(e, open)}
                    />
                    {!image.id && (
                      <Badge
                        pos="absolute"
                        bottom={8}
                        right={8}
                        variant="white"
                        c="dark"
                        style={{
                          opacity: "70%",
                          cursor: "pointer",
                        }}
                        onClick={(event) => {
                          event.stopPropagation();
                          setFeedbackImage({
                            ...image,
                            id: `gallery-image-${index}`,
                          });
                          toggleFeedback();
                        }}
                      >
                        <Flex align="center" gap="sm">
                          <IconFlag size={14} />
                          {t("car.carburateResource")}
                        </Flex>
                      </Badge>
                    )}
                  </>
                )}
              </Box>
            </Carousel.Slide>
          ))}
          slideProps={{ m: 0 }}
        />
        {images.length > 1 && (
          <Group className={classNames.previewContainer}>
            {images.slice(0, showPreviews).map((image, index) =>
              image.path.includes(env.API_URL) ? (
                <LoadedImage
                  key={index}
                  hideLoader
                  image={image}
                  //alt={image.name}
                  className={`${classNames.previewImage} ${index === activeIndex ? classNames.activePreview : ""}`}
                  onClick={() => onSlideChange(index)}
                  radius="md"
                />
              ) : (
                <Image
                  key={index}
                  src={image.path}
                  alt={image.name}
                  className={`${classNames.previewImage} ${index === activeIndex ? classNames.activePreview : ""}`}
                  onClick={() => onSlideChange(index)}
                  radius="md"
                />
              )
            )}
            {hasMoreImages && (
              <Box className={classNames.moreImages} onClick={() => onSlideChange(maxPreviews)}>
                <Text>+{moreImagesCount}</Text>
              </Box>
            )}
          </Group>
        )}
      </Box>
      <Modal
        opened={opened}
        onClose={close}
        title={modalTitle}
        centered
        size="xl"
        classNames={{
          title: classNames.mantineModalTitle,
        }}
      >
        <Grid gutter="lg" className={classNames.galleryGrid} p="xl">
          {images.map((image, index) => {
            const isWide = index % 3 === 0;
            const colSpan = isWide ? 12 : 6;

            return (
              <Grid.Col span={colSpan} key={image.id}>
                <Paper withBorder radius="md" style={{ position: "relative", overflow: "hidden" }}>
                  <Box
                    style={{
                      height: 260,
                      width: "100%",
                    }}
                  >
                    {image.path.includes(env.API_URL) ? (
                      <LoadedImage
                        image={image}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <Image
                        src={image.path}
                        alt={image.name}
                        fit="cover"
                        radius="md"
                        h="100%"
                        w="100%"
                      />
                    )}
                  </Box>

                  {!image.id && (
                    <Badge
                      pos="absolute"
                      bottom={8}
                      right={8}
                      variant="white"
                      c="dark"
                      style={{
                        opacity: "70%",
                        cursor: "pointer",
                      }}
                      onClick={() => {
                        setFeedbackImage({
                          ...image,
                          id: `gallery-image-${index}`,
                        });
                        toggleFeedback();
                      }}
                    >
                      <Flex align="center" gap="sm">
                        <IconFlag size={14} />
                        {t("car.carburateResource")}
                      </Flex>
                    </Badge>
                  )}
                </Paper>
              </Grid.Col>
            );
          })}
        </Grid>
      </Modal>

      <UserFeedback
        title={t("feedback.wrongImageTitle")}
        description={t("feedback.wrongImageDescription")}
        opened={feedbackOpened}
        image={feedbackImage}
        onOpenedChange={toggleFeedback}
      />
    </>
  );
};
